import React from 'react';
import { createPortal } from 'react-dom';
import { Fade, Toast } from 'react-bootstrap-5';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';

const ToastTransition = React.forwardRef((props, ref) => (
  <Fade {...props} appear mountOnEnter ref={ref} unmountOnExit />
));
ToastTransition.displayName = 'ToastTransition';

export const variants = {
  danger: { icon: faCircleExclamation },
  info: { icon: faCircleInfo },
  success: { icon: faCircleCheck },
  warning: { icon: faTriangleExclamation },
};

const STACK_CONTAINER_ATTRIBUTE = 'data-snackbar-stack';

const getOrCreateStackContainer = (portalRoot, position) => {
  const selector = `[${STACK_CONTAINER_ATTRIBUTE}="${position}"]`;
  let container = portalRoot.querySelector(selector);

  if (!container) {
    container = document.createElement('div');
    container.setAttribute(STACK_CONTAINER_ATTRIBUTE, position);
    container.className = [
      'toast-container',
      'p-3',
      'position-fixed',
      'end-0',
      position === 'top' ? null : 'bottom-0',
      'd-flex',
      'gap-2',
      'align-items-end',
      position === 'top' ? 'flex-column-reverse' : 'flex-column',
    ]
      .filter(Boolean)
      .join(' ');
    container.style.top = position === 'top' ? '120px' : '';
    portalRoot.appendChild(container);
  }

  return container;
};

export default function Snackbar({
  autohide = true,
  delay = 4000,
  icon,
  portalElementSelector = 'body',
  position = 'bottom',
  show = false,
  text,
  variant,
  onClose,
}) {
  const portalRoot = document.querySelector(portalElementSelector);
  if (!portalRoot) return null;

  const stackContainer = getOrCreateStackContainer(portalRoot, position);

  const resolvedIcon = icon ?? variants[variant]?.icon;

  // Freeze the displayed content while visible / exiting.
  const [rendered, setRendered] = React.useState(() => ({
    text,
    variant,
    icon: resolvedIcon,
  }));

  // Used to force a fresh Toast instance so transitions/autohide restart.
  const [toastKey, setToastKey] = React.useState(0);

  const prevShowRef = React.useRef(show);
  const renderedRef = React.useRef({ text, variant, icon: resolvedIcon });

  // Guard against stale onExited clearing content after reopening.
  const showRef = React.useRef(show);
  React.useEffect(() => {
    showRef.current = show;
  }, [show]);

  React.useEffect(() => {
    const prevShow = prevShowRef.current;
    prevShowRef.current = show;

    const contentChanged =
      renderedRef.current.text !== text ||
      renderedRef.current.variant !== variant ||
      renderedRef.current.icon !== resolvedIcon;

    // Refresh content (and force a remount) when:
    // - toast opens
    // - toast is already open but the content changes
    if (show && (!prevShow || contentChanged)) {
      setRendered({ text, variant, icon: resolvedIcon });
      setToastKey(k => k + 1);
      renderedRef.current = { text, variant, icon: resolvedIcon };
    }
  }, [show, text, variant, resolvedIcon]);

  const handleExited = React.useCallback(() => {
    // If we reopened before exit finished, ignore this stale exit callback.
    if (showRef.current) return;

    setRendered({ text: null, variant: null, icon: null });
  }, []);

  const uiVariant =
    rendered.variant === 'danger' ? 'destructive' : rendered.variant;

  const iconClassName = uiVariant ? `text-${uiVariant}` : null;

  const shouldRender =
    show ||
    rendered.text !== null ||
    rendered.variant !== null ||
    rendered.icon !== null;

  if (!shouldRender) return null;

  return createPortal(
    <Toast
      aria-label="notification"
      autohide={autohide}
      className={[
        'ms-auto',
        'w-auto',
        uiVariant ? `border border-${uiVariant}` : null,
      ]
        .filter(Boolean)
        .join(' ')}
      delay={delay}
      key={toastKey}
      show={show}
      transition={ToastTransition}
      onClose={onClose}
      onExited={handleExited}
    >
      <Toast.Body className="d-flex align-items-center gap-2 py-2 px-3">
        {rendered.icon && (
          <FontAwesomeIcon className={iconClassName} icon={rendered.icon} />
        )}

        <div className="flex-grow-1">{rendered.text}</div>

        <button
          aria-label="Close"
          className="btn-close"
          type="button"
          onClick={onClose}
        />
      </Toast.Body>
    </Toast>,
    stackContainer,
  );
}

Snackbar.propTypes = {
  autohide: PropTypes.bool,
  delay: PropTypes.number,
  icon: PropTypes.object,
  portalElementSelector: PropTypes.string,
  position: PropTypes.oneOf(['bottom', 'top']),
  show: PropTypes.bool,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  variant: PropTypes.oneOf(Object.keys(variants)),
  onClose: PropTypes.func.isRequired,
};
